/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Whisper Subtitle & Transcription Studio Main View (Task 10.5.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useMemo } from 'react';
import {
  AudioWaveform,
  FileText,
  Clock,
  Globe2,
  Copy,
  Check,
  Download,
  ListFilter,
} from 'lucide-react';
import { useTranslation } from '../i18n';
import {
  useSubtitleStudioStore,
  formatTimecode,
} from '../stores/useSubtitleStudioStore';
import { AudioUploaderZone } from '../components/subtitles/AudioUploaderZone';
import { WaveformTimelineScrubber } from '../components/subtitles/WaveformTimelineScrubber';
import { SubtitleTransportBar } from '../components/subtitles/SubtitleTransportBar';
import { SubtitleSegmentList } from '../components/subtitles/SubtitleSegmentList';
import { SubtitleShiftModal } from '../components/subtitles/SubtitleShiftModal';
import { SubtitleExportModal } from '../components/subtitles/SubtitleExportModal';

export const SubtitleStudioView: React.FC = () => {
  const { t } = useTranslation();
  const {
    cues,
    audioFile,
    detectedLanguage,
    activeTab,
    setActiveTab,
    setExportModalOpen,
  } = useSubtitleStudioStore();

  const [copiedPlainText, setCopiedPlainText] = React.useState(false);

  const totalDuration = audioFile?.durationSec || 36.5;

  const totalWords = useMemo(() => {
    return cues.reduce((sum, c) => sum + (c.text.trim().split(/\s+/).filter(Boolean).length || 0), 0);
  }, [cues]);

  const fullPlainText = useMemo(() => {
    return cues.map((c) => c.text.trim()).filter(Boolean).join('\n\n');
  }, [cues]);

  const handleCopyPlainText = async () => {
    try {
      await navigator.clipboard.writeText(fullPlainText);
      setCopiedPlainText(true);
      setTimeout(() => setCopiedPlainText(false), 2000);
    } catch {
      // Fallback
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* 1. Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="p-2 rounded-xl bg-primary/10 text-primary">
              <AudioWaveform className="size-5" />
            </span>
            <h1 className="text-xl font-bold text-text">
              {t('subtitles.studioTitle')}
            </h1>
            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              Whisper v3
            </span>
          </div>
          <p className="text-xs text-text-muted max-w-2xl">
            {t('subtitles.studioSubtitle')}
          </p>
        </div>

        {/* Action Button */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setExportModalOpen(true)}
            className="px-4 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-sm flex items-center gap-2"
          >
            <Download className="size-4" />
            <span>{t('subtitles.tabExport')}</span>
          </button>
        </div>
      </div>

      {/* 2. Top Metrics Summary Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-surface rounded-xl border border-border p-3 flex items-center gap-3">
          <span className="p-2 rounded-lg bg-primary/10 text-primary">
            <Clock className="size-4" />
          </span>
          <div>
            <div className="text-[11px] text-text-muted font-medium">
              {t('subtitles.statsDuration')}
            </div>
            <div className="text-sm font-bold text-text font-mono">
              {formatTimecode(totalDuration)}
            </div>
          </div>
        </div>

        <div className="bg-surface rounded-xl border border-border p-3 flex items-center gap-3">
          <span className="p-2 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
            <FileText className="size-4" />
          </span>
          <div>
            <div className="text-[11px] text-text-muted font-medium">
              {t('subtitles.statsWords')}
            </div>
            <div className="text-sm font-bold text-text">
              {totalWords.toLocaleString()}
            </div>
          </div>
        </div>

        <div className="bg-surface rounded-xl border border-border p-3 flex items-center gap-3">
          <span className="p-2 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
            <ListFilter className="size-4" />
          </span>
          <div>
            <div className="text-[11px] text-text-muted font-medium">
              {t('subtitles.statsCues')}
            </div>
            <div className="text-sm font-bold text-text">
              {cues.length}
            </div>
          </div>
        </div>

        <div className="bg-surface rounded-xl border border-border p-3 flex items-center gap-3">
          <span className="p-2 rounded-lg bg-sky-500/10 text-sky-600 dark:text-sky-400">
            <Globe2 className="size-4" />
          </span>
          <div>
            <div className="text-[11px] text-text-muted font-medium">
              {t('subtitles.statsLanguage')}
            </div>
            <div className="text-sm font-bold text-text uppercase">
              {detectedLanguage || 'VI'}
            </div>
          </div>
        </div>
      </div>

      {/* 3. Audio & Whisper Uploader Zone */}
      <AudioUploaderZone />

      {/* 4. Tab Navigation */}
      <div className="flex items-center gap-2 border-b border-border pb-1">
        <button
          type="button"
          onClick={() => setActiveTab('editor')}
          className={`px-4 py-2 text-xs font-semibold rounded-t-xl transition-all border-b-2 flex items-center gap-1.5 ${
            activeTab === 'editor'
              ? 'border-primary text-primary'
              : 'border-transparent text-text-muted hover:text-text'
          }`}
        >
          <AudioWaveform className="size-4" />
          <span>{t('subtitles.tabEditor')}</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('plain-text')}
          className={`px-4 py-2 text-xs font-semibold rounded-t-xl transition-all border-b-2 flex items-center gap-1.5 ${
            activeTab === 'plain-text'
              ? 'border-primary text-primary'
              : 'border-transparent text-text-muted hover:text-text'
          }`}
        >
          <FileText className="size-4" />
          <span>{t('subtitles.tabPlainText')}</span>
        </button>
      </div>

      {/* 5. Tab Content: Editor vs Plain Text */}
      {activeTab === 'editor' ? (
        <div className="space-y-6">
          {/* Waveform Scrubber */}
          <WaveformTimelineScrubber />

          {/* Master Transport Bar */}
          <SubtitleTransportBar />

          {/* Segment List Editor */}
          <SubtitleSegmentList />
        </div>
      ) : (
        /* Plain Text Tab */
        <div className="bg-surface rounded-2xl border border-border p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-border">
            <div>
              <h3 className="text-sm font-semibold text-text">
                {t('subtitles.formatTxt')}
              </h3>
              <p className="text-xs text-text-muted">
                {totalWords} words • {fullPlainText.length} characters
              </p>
            </div>
            <button
              type="button"
              onClick={handleCopyPlainText}
              className="px-3 py-1.5 text-xs font-medium rounded-xl border border-border hover:bg-surface-subtle text-text transition-colors flex items-center gap-1.5"
            >
              {copiedPlainText ? (
                <>
                  <Check className="size-3.5 text-emerald-500" />
                  <span className="text-emerald-500">{t('subtitles.copiedToast')}</span>
                </>
              ) : (
                <>
                  <Copy className="size-3.5" />
                  <span>{t('subtitles.copyBtn')}</span>
                </>
              )}
            </button>
          </div>

          <div className="rounded-xl border border-border bg-surface-subtle p-5 font-sans text-xs text-text whitespace-pre-wrap leading-relaxed max-h-[500px] overflow-y-auto select-all">
            {fullPlainText || t('subtitles.emptyCuesTitle')}
          </div>
        </div>
      )}

      {/* 6. Modals */}
      <SubtitleShiftModal />
      <SubtitleExportModal />
    </div>
  );
};
